/**
 * The About page's entity copy, in one place: who the founders are, the
 * three facts, and the origin story. src/rebuild/AboutPage.tsx renders from
 * these records (photos stay in the component, they are asset imports) and
 * scripts/prerender.cjs prints the same records into the served HTML, so a
 * crawler that does not run JavaScript reads the founders' names, titles and
 * bios. Until 2026-09-12 they were JSX-only; the served /about carried the
 * title and the intro sentence (which name both founders) and none of the
 * founder records: roles, headlines, bios, focus lists, LinkedIn links.
 *
 * Keep this a plain TypeScript module with no JSX and no runtime imports:
 * scripts/prerender.cjs transpiles it in-process with esbuild, the same way
 * it reads src/data/tampa.ts and src/data/solutions.ts. Dominik Kloda is the
 * Founder and Jake Daly the Co-founder; the titles are not to be rephrased.
 */
export type AboutFounder = {
  id: "dominik" | "jake";
  name: string;
  role: string;
  short: string;
  linkedin: string;
  headline: string;
  bio: readonly string[];
  focus: readonly string[];
};

export const ABOUT_FOUNDERS: readonly AboutFounder[] = [
  {
    id: "dominik",
    name: "Dominik Kloda",
    role: "Founder · Technology & execution",
    short: "founder",
    linkedin: "https://www.linkedin.com/in/dominik-kloda/",
    headline: "The person behind the system.",
    bio: [
      "Dominik Kloda is the Founder of Chleb AI. The idea started with him, and he leads the technical build: the receptionist, the call flows, the CRM, the websites. Before Chleb, he built and operated online businesses, applications, and automation. That experience gave him a practical view of technology: it needs to be reliable, solve a real problem, and make the work easier.",
      "At Chleb, his work spans AI automation, application development, and website design and development. He connects the pieces behind the first conversation, from a business-specific call flow to the records and follow-up that help a team act on it.",
    ],
    focus: ["AI automation", "Applications", "Websites & integrations"],
  },
  {
    id: "jake",
    name: "Jake Daly",
    role: "Co-founder · Business & operations",
    short: "co-founder",
    linkedin: "https://www.linkedin.com/in/jakeadaly/",
    headline: "The work behind the standard.",
    bio: [
      "Jake is an entrepreneur and former NCAA student athlete. His businesses have spanned consulting, app development, home services, and car detailing. Working in those businesses meant seeing firsthand how much can depend on answering one call or following up at the right time.",
      "That work put him in front of the same problem every day, and it shaped what Chleb pays attention to: capable service businesses need a front office that can keep up with them. Alongside Dominik, Jake brings an operator’s perspective to the customer experience, the handoff to the team, and the everyday details that make a system useful.",
    ],
    focus: ["Service businesses", "Customer experience", "Business operations"],
  },
];

/** The three facts under the founders' portraits. */
export const ABOUT_FACTS: readonly (readonly [string, string])[] = [
  ["Our beginning", "January 2026"],
  ["Our home", "Tampa, Florida"],
  ["Our focus", "Service businesses across the U.S."],
];

export const ABOUT_ORIGIN = {
  /** Rendered with line breaks on the page; printed as one sentence pair in the served HTML. */
  heading: ["The work was good.", "The follow-up", "wasn’t."],
  lead: "A ringing phone during a job. A form left until Monday. A customer who moves on.",
  paragraphs: [
    "Dominik saw it while building online businesses, where a missed lead was a missed sale. Jake saw it while running car detailing and home service businesses. Good work was getting lost between the first call and the booked job.",
    "Dominik started Chleb AI in January 2026 to close that gap, with Jake alongside him on operations. Today, we connect AI call answering, website chat, CRM, booking, and follow-up around the everyday work of service businesses.",
    "The aim is straightforward: help the customer get an answer, and help the team know what happens next.",
  ],
} as const;

export const ABOUT_FOUNDERS_SECTION = {
  heading: ["Meet the people", "who put it together."],
  sub: ["Two different backgrounds.", "The same practical standard."],
} as const;
